/**
 * Feature Flags Configuration
 *
 * Central place to toggle features on/off. Uses PostHog feature flags
 * when available, falls back to static defaults here.
 *
 * To re-enable a feature:
 *   1. Change the default value here, OR
 *   2. Enable via PostHog dashboard (overrides the default)
 */

import { getFeatureFlag } from '../lib/posthog';

// Static defaults — these are used when PostHog is unavailable
const FLAG_DEFAULTS: Record<string, boolean> = {
  'unclaimed-property': false,  // Disabled — enable when unclaimed property feature is ready
  'demo-mode': false,           // Demo mode — shows all claims including partnered_pending
  'milestones': false,          // Disabled — earnings tracker, achievements, milestone celebrations
  'family-plans': false,        // Disabled — family plan has no member sharing model yet
  'funnel-identity-handoff-v1': true,
  'funnel-result-reveal-v1': true,
  'funnel-canonical-paywall-v1': true,
  'funnel-analytics-schema-v1': true,
};

const FLAG_ENV_KEYS: Record<string, string> = {
  'funnel-identity-handoff-v1': 'VITE_FLAG_IDENTITY_HANDOFF',
  'funnel-result-reveal-v1': 'VITE_FLAG_RESULT_REVEAL',
  'funnel-canonical-paywall-v1': 'VITE_FLAG_CANONICAL_PAYWALL',
  'funnel-analytics-schema-v1': 'VITE_FLAG_ANALYTICS_SCHEMA',
};

/**
 * Check if a feature is enabled.
 *
 * Priority: PostHog remote flag → static default → false
 */
export function isEnabled(flag: string): boolean {
  const envKey = FLAG_ENV_KEYS[flag];
  const envValue = envKey ? (import.meta.env as Record<string, string | undefined>)[envKey] : undefined;
  if (envValue === 'true') return true;
  if (envValue === 'false') return false;

  // Try PostHog first (returns false if PostHog isn't loaded)
  const posthogValue = getFeatureFlag(flag);
  if (typeof posthogValue === 'boolean') return posthogValue;

  // Fall back to static default
  return FLAG_DEFAULTS[flag] ?? false;
}

/**
 * Check if demo mode is active.
 * Demo mode shows all claims (including partnered_pending) with full CTAs.
 * Activated via: PostHog flag, URL param ?demo=true, or static default.
 */
export function isDemoMode(): boolean {
  // Check URL param first (for quick advertiser demos)
  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('demo') === 'true') return true;
    } catch {
      // Ignore malformed browser URLs and use the configured flag authority.
    }
  }
  return isEnabled('demo-mode');
}

// Named feature checks for convenience
export const Features = {
  get unclaimedProperty() {
    return isEnabled('unclaimed-property');
  },
  get demoMode() {
    return isDemoMode();
  },
  get milestones() {
    return isEnabled('milestones');
  },
  get familyPlans() {
    return isEnabled('family-plans');
  },
  get identityHandoff() {
    return isEnabled('funnel-identity-handoff-v1');
  },
  get resultReveal() {
    return isEnabled('funnel-result-reveal-v1');
  },
  get canonicalPaywall() {
    return isEnabled('funnel-canonical-paywall-v1');
  },
  get analyticsSchemaV1() {
    return isEnabled('funnel-analytics-schema-v1');
  },
};
